import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "./ui/Button";
import { useAuth } from "../lib/useAuth";
import { useThemeMode } from "../lib/theme-mode";

/**
 * Minimal top bar when the sidebar is hidden for first-run / wizard.
 * Sign out, Settings and theme stay reachable without the rail.
 */
export default function CompactChrome() {
  const { user, guest, logout } = useAuth();
  const { resolvedMode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const label =
    user?.email ||
    (typeof guest?.name === "string" ? guest.name.trim() : "") ||
    "Signed in";
  const nextMode = resolvedMode === "dark" ? "light" : "dark";

  return (
    <header
      className="sticky top-0 z-40 flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-od-border bg-od-surface px-4 pb-2 sm:flex-nowrap sm:pb-0"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingLeft: "max(1rem, env(safe-area-inset-left))",
        paddingRight: "max(1rem, env(safe-area-inset-right))",
        minHeight: "calc(3rem + env(safe-area-inset-top))",
      }}
      data-testid="compact-chrome"
    >
      <a
        href="https://onlydash.io"
        className="inline-flex min-h-11 shrink-0 items-center text-sm font-semibold text-od-text rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary sm:min-h-0 [@media(pointer:coarse)]:min-h-11"
      >
        onlydash<span className="text-od-primary-text">.</span>
      </a>
      <span
        className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded-full text-xs font-semibold tracking-wide bg-od-primary/15 text-od-primary-text border border-od-primary/30"
        data-testid="beta-label"
      >
        Beta
      </span>
      <span className="order-last w-full min-w-0 truncate text-xs text-od-muted sm:order-none sm:ml-auto sm:w-auto sm:max-w-[40%]" title={label}>
        {label}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-1.5 sm:ml-0">
        <Button
          variant="ghost"
          size="icon"
          title={`Switch to ${nextMode} mode`}
          aria-label={`Switch to ${nextMode} mode`}
          className="focus-visible:ring-2 focus-visible:ring-od-primary [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
          onClick={toggleMode}
          data-testid="compact-theme-toggle"
        >
          {resolvedMode === "dark" ? (
            <LightModeIcon sx={{ fontSize: 18 }} />
          ) : (
            <DarkModeIcon sx={{ fontSize: 18 }} />
          )}
        </Button>
        <Link
          to="/settings"
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-od-muted hover:bg-od-surface-2 hover:text-od-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-od-primary sm:h-8 sm:w-8 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11"
          title="Settings"
          aria-label="Settings"
          data-testid="compact-settings"
        >
          <SettingsIcon sx={{ fontSize: 18 }} />
        </Link>
        <Button
          variant="secondary"
          size="sm"
          title="Sign out"
          aria-label="Sign out"
          className="focus-visible:ring-2 focus-visible:ring-od-primary [@media(pointer:coarse)]:min-h-11"
          data-testid="compact-sign-out"
          onClick={() => {
            logout();
            navigate("/auth");
          }}
        >
          Sign out
        </Button>
      </div>
    </header>
  );
}
